import { getAddress, keccak256, toHex } from "viem";
import { z } from "zod";
import { ethereumAddressSchema } from "./schema.js";
const decimalBlockSchema = z.string().regex(/^(0|[1-9]\d*)$/);
const bytes32Schema = z
    .string()
    .regex(/^0x[0-9a-fA-F]{64}$/)
    .transform((value) => value);
const addressRecordSchema = z.record(z.string().min(1), ethereumAddressSchema).readonly();
const implementationLinksSchema = z
    .object({
    factory: addressRecordSchema,
    marketplace: addressRecordSchema,
    projectCore: addressRecordSchema,
    nandNft: addressRecordSchema,
    minerNft: addressRecordSchema,
})
    .readonly();
const implementationCodeHashesSchema = z
    .object({
    factory: bytes32Schema,
    marketplace: bytes32Schema,
    projectCore: bytes32Schema,
    nandNft: bytes32Schema,
    minerNft: bytes32Schema,
})
    .readonly();
const deploymentManifestCommon = {
    chainId: z.number().int().positive().safe(),
    network: z.string().min(1),
    deploymentBlock: decimalBlockSchema,
    deploymentBlockHash: bytes32Schema,
    deployedAt: z.string().datetime(),
    deployer: ethereumAddressSchema,
    protocol: z
        .object({
        factory: ethereumAddressSchema,
        marketplace: ethereumAddressSchema,
        safe: ethereumAddressSchema,
        treasury: ethereumAddressSchema,
    })
        .readonly(),
    pancakeV2: z.object({ factory: ethereumAddressSchema, wbnb: ethereumAddressSchema }).readonly(),
    implementations: addressRecordSchema,
    beacons: addressRecordSchema,
    libraries: addressRecordSchema,
};
const deploymentManifestBodyV1Object = z.object({
    schemaVersion: z.literal("1"),
    ...deploymentManifestCommon,
});
const deploymentManifestBodyV2Object = z.object({
    schemaVersion: z.literal("2"),
    ...deploymentManifestCommon,
    revision: z.number().int().nonnegative().safe(),
    previousChecksum: bytes32Schema.nullable(),
    implementationLinks: implementationLinksSchema,
    implementationCodeHashes: implementationCodeHashesSchema,
});
const deploymentManifestBodySchema = z.discriminatedUnion("schemaVersion", [
    deploymentManifestBodyV1Object.readonly(),
    deploymentManifestBodyV2Object.readonly(),
]);
export const deploymentManifestSchema = z.discriminatedUnion("schemaVersion", [
    deploymentManifestBodyV1Object.extend({ checksum: bytes32Schema }).readonly(),
    deploymentManifestBodyV2Object.extend({ checksum: bytes32Schema }).readonly(),
]);
function canonicalJson(value) {
    if (value === null || typeof value === "boolean" || typeof value === "string") {
        return JSON.stringify(value);
    }
    if (typeof value === "number") {
        if (!Number.isFinite(value))
            throw new TypeError("Deployment manifest contains a non-finite number");
        return JSON.stringify(value);
    }
    if (Array.isArray(value))
        return `[${value.map(canonicalJson).join(",")}]`;
    if (typeof value !== "object")
        throw new TypeError("Deployment manifest is not canonical JSON");
    const record = value;
    return `{${Object.keys(record)
        .sort()
        .map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`)
        .join(",")}}`;
}
export function deploymentManifestChecksum(body) {
    const normalized = deploymentManifestBodySchema.parse(body);
    return keccak256(toHex(canonicalJson(normalized)));
}
export function createDeploymentManifest(body) {
    const normalized = deploymentManifestBodySchema.parse(body);
    return deploymentManifestSchema.parse({
        ...normalized,
        checksum: deploymentManifestChecksum(normalized),
    });
}
export function verifyDeploymentManifest(value) {
    const manifest = deploymentManifestSchema.parse(value);
    const { checksum, ...body } = manifest;
    if (deploymentManifestChecksum(body) !== checksum) {
        throw new Error(`Deployment manifest checksum mismatch for chain ${manifest.chainId}`);
    }
    return manifest;
}
export function manifestAddress(manifest, key) {
    return getAddress(manifest.protocol[key]);
}
//# sourceMappingURL=deployment.js.map